import React, { useRef, useState } from "react";
import ImportantTerms from "./ImportantTerms";
import CourierSelectionRate from "./CourierSelectionRate";
import costEstimationimg from "../../../assets/undraw_our_solution_re_8yk6 1.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FiChevronDown } from "react-icons/fi";

const REACT_APP_URL = process.env.REACT_APP_BACKEND_URL;

const CostEstimation = () => {
  const resultSectionRef = useRef(null);

  const [formData, setFormData] = useState({
    shipmentType: "Forward",
    pickUpPincode: "",
    deliveryPincode: "",
    weight: "",
    declaredValue: "",
    paymentType: "Prepaid",
    dimensions: { length: "", breadth: "", height: "" },
    noOfBox: "",
    weightPerBox: "",
  });

  const [locationData, setLocationData] = useState({
    from_city: "",
    from_state: "",
    to_city: "",
    to_state: "",
  });

  const [courierData, setCourierData] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navigate = useNavigate();

  const fetchCityStateFromPincode = async (pincode) => {
    try {
      if (!pincode || pincode.length !== 6) return { city: "", state: "" };
      const token = document.cookie.split("=")[1];
      const response = await axios.get(`${REACT_APP_URL}/order/pincode/${pincode}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response.data) {
        return { city: response.data.city, state: response.data.state };
      }
      return { city: "", state: "" };
    } catch (error) {
      console.error("Error fetching city/state for pincode:", error);
      return { city: "", state: "" };
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name.startsWith("dimensions")) {
      const dimensionKey = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimensionKey]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "pickUpPincode" && value.length === 6) {
        const { city, state } = await fetchCityStateFromPincode(value);
        setLocationData((prev) => ({
          ...prev,
          from_city: city,
          from_state: state,
        }));
      } else if (name === "deliveryPincode" && value.length === 6) {
        const { city, state } = await fetchCityStateFromPincode(value);
        setLocationData((prev) => ({
          ...prev,
          to_city: city,
          to_state: state,
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const length = Number(formData.dimensions.length);
    const breadth = Number(formData.dimensions.breadth);
    const height = Number(formData.dimensions.height);
    const actualWeight = Number(formData.weight);

    const volumetricWeight = (length * breadth * height) / 5000;
    const applicableWeight = Math.max(actualWeight, volumetricWeight);

    const payload = {
      pickUpPincode: formData.pickUpPincode,
      deliveryPincode: formData.deliveryPincode,
      weight: actualWeight,
      declaredValue: formData.declaredValue,
      paymentType: formData.paymentType,
      length: length,
      width: breadth,
      height: height,
      applicableWeight: applicableWeight,
      noOfBox: Number(formData.noOfBox) || 1,
      weightPerBox: Number(formData.weightPerBox) || applicableWeight,
      from_city: locationData.from_city,
      from_state: locationData.from_state,
      to_city: locationData.to_city,
      to_state: locationData.to_state,
    };

    console.log("Payload for API:", payload);

    try {
      setLoading(true);
      setHasFetched(true);

      const [, token] = document.cookie.split("=");
      const response = await axios.post(
        `${REACT_APP_URL}/Shiprocket/calculateShiprocketCharges`,
        payload,
        { headers: { authorization: `Bearer ${token}` } }
      );

      const rates = response.data.updatedRates || [];

      if (rates.length === 0) {
        toast.warn("No shipping rates available for selected inputs");
        setIsDataFetched(false);
        setCourierData([]);
        return;
      }

      // ✅ Use backend's final data directly
      setCourierData(rates);
      setIsDataFetched(true);

      setTimeout(() => {
        resultSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 300);

      console.log("Shipping Rates (from backend):", rates);
    } catch (err) {
      console.error("Error calculating shipping rates:", err);
      toast.error("Failed to fetch shipping rates. Please try again.");
      setIsDataFetched(false);
      setCourierData([]);
    } finally {
      setLoading(false);
      setHasFetched(false);
    }
  };

  return (
    <div className="py-2 sm:px-2">
      <div className="mb-4">
        <h1 className="text-[12px] md:text-[18px] font-[600] text-gray-700">
          Shipping Rates Calculator
        </h1>
      </div>

      <div className="sm:bg-white mx-auto w-full max-w-full rounded-lg sm:shadow-sm sm:p-4 flex flex-col md:flex-row lg:shadow-[0_2px_8px_rgba(12,187,125,0.3)] md:shadow-sm ">
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 w-full md:w-1/2" noValidate>
          <div className="grid grid-cols-2 gap-6">
            {/* Pickup pincode */}
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Pick-up Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="pickUpPincode"
                required
                value={formData.pickUpPincode}
                onChange={handleChange}
                placeholder="6 Digit Pincode"
                pattern="^\d{6}$"
                maxLength={6}
                onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ""); }}
                className="w-full border border-gray-300 rounded px-3 h-9 text-[12px]"
              />
            </div>
            {/* Delivery pincode */}
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Delivery Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="deliveryPincode"
                required
                value={formData.deliveryPincode}
                onChange={handleChange}
                placeholder="6 Digit Pincode"
                pattern="^\d{6}$"
                maxLength={6}
                onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ""); }}
                className="w-full border border-gray-300 rounded px-3 h-9 text-[12px]"
              />
            </div>
          </div>

          {/* Weight & Declared Value */}
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div className="relative">
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Payment Type <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center justify-between border border-gray-300 rounded px-3 h-9 cursor-pointer text-gray-500 text-[12px]"
              >
                {formData.paymentType}
                <FiChevronDown className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </div>
              {dropdownOpen && (
                <div className="absolute bg-white border border-gray-300 rounded mt-1 w-full shadow z-10">
                  {["Prepaid", "COD"].map((option) => (
                    <div
                      key={option}
                      onClick={() => {
                        setFormData({ ...formData, paymentType: option });
                        setDropdownOpen(false);
                      }}
                      className={`px-3 py-2 cursor-pointer text-[12px] ${
                        formData.paymentType === option ? "bg-green-100 font-semibold" : ""
                      }`}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Declared Value (₹) <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <input
                  type="number"
                  name="declaredValue"
                  required
                  min={0}
                  step="any"
                  value={formData.declaredValue}
                  onChange={handleChange}
                  placeholder="E.g., 1000"
                  className="w-full border border-gray-300 rounded-l px-3 h-9 text-[12px]"
                />
                <div className="bg-[#0cbb7d] px-3 py-2 rounded-r text-white text-[12px]">₹</div>
              </div>
            </div>
          </div>

          {/* No. of Boxes and Weight per box */}
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                No. of Box <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="noOfBox"
                required
                min={1}
                step={1}
                value={formData.noOfBox}
                onChange={handleChange}
                placeholder="E.g., 1"
                className="w-full border border-gray-300 rounded px-3 h-9 text-[12px]"
              />
            </div>
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Weight Per Box (Kg) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="weightPerBox"
                required
                min={0}
                step="any"
                value={formData.weightPerBox}
                onChange={handleChange}
                placeholder="E.g., 1"
                className="w-full border border-gray-300 rounded px-3 h-9 text-[12px]"
              />
            </div>
          </div>

          
          <div className="grid grid-cols-1 gap-6 mt-4">
            

            {/* Dimensions */}
            <div>
              <label className="block font-[600] mb-2 text-gray-500 text-[12px]">
                Dimensions (CM) <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" name="dimensions.length" required min={0} step="any"
                  value={formData.dimensions.length} onChange={handleChange} placeholder="L"
                  className="w-full border border-gray-300 rounded px-2 h-9 text-[12px]" />
                <input type="number" name="dimensions.breadth" required min={0} step="any"
                  value={formData.dimensions.breadth} onChange={handleChange} placeholder="B"
                  className="w-full border border-gray-300 rounded px-2 h-9 text-[12px]" />
                <input type="number" name="dimensions.height" required min={0} step="any"
                  value={formData.dimensions.height} onChange={handleChange} placeholder="H"
                  className="w-full border border-gray-300 rounded px-2 h-9 text-[12px]" />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              type="submit"
              className="bg-[#0cbb7d] text-white w-full py-2 rounded text-[14px] font-semibold"
              disabled={loading}
            >
              {loading ? "Calculating..." : "Calculate"}
            </button>
          </div>
        </form>

        <div className="hidden md:block w-1/2 flex justify-center items-center">
          <img src={costEstimationimg} alt="Illustration"
            className="max-w-[300px]" loading="lazy" />
        </div>
      </div>

      <div ref={resultSectionRef} className="mt-6">
        {isDataFetched && (
          <CourierSelectionRate
            plan={courierData.filter((item) => item.rate !== null)}
            isDataFetched={isDataFetched}
            loading={loading}
            hasFetched={hasFetched}
          />
        )}
      </div>

      <div className="mt-6">
        <ImportantTerms />
      </div>
    </div>
  );
};

export default CostEstimation;
